import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';

import GytrImportStep from './GytrImportStep';
import GytrManualEntryStep from './GytrManualEntryStep';
import GytrEmailGateStep from './GytrEmailGateStep';
import GytrProcessingStep from './GytrProcessingStep';
import GytrRevealStep from './GytrRevealStep';
import GytrTransferStep from './GytrTransferStep';
import StepProgressTopBar from '@components/quiz/shared/StepProgressTopBar';
import PaywallBlock from '@components/quiz/PaywallBlock';
import { useGytr } from '@components/quiz/GytrProvider';
import { buildGytrPaywallContext } from '@components/quiz/GytrProvider/utils/paywall-context';
import { useFunnelEmailCapture } from '@hooks/useFunnelEmailCapture';
import { awaitEmailCaptureWithinBudget } from '@components/quiz/shared/await-email-capture';
import useQuizHashTracking from '@hooks/useQuizHashTracking';
import { INITIAL_QUIZ_STATE } from '@lib/page-utils/Quiz';
import { isAppRelativeReturnTo } from '@lib/auth0/return-to';
import {
  captureFunnelEmailCaptured,
  captureFunnelStarted,
  captureFunnelStepCompleted
} from '@lib/analytics/funnel-events';
import {
  deriveProgressSteps,
  getCurrentProgressStep
} from '@lib/page-utils/Gytr/progress';
import type { AnalyticsFunnel } from '@lib/analytics/events';
import type { SanityQuizBlock, SanityQuizPage } from '@lib/sanity/types';

type GytrFlowControllerProps = {
  quizPage: SanityQuizPage;
};

// Blocks reachable before any squad has been imported/built.
const NO_TEAM_BLOCK_TYPES = new Set([
  'gytrImportStepBlock',
  'gytrManualEntryStepBlock'
]);

// The analytics funnel this controller drives (FFH-2372).
const GYTR_FUNNEL: AnalyticsFunnel = 'team_rating';

/**
 * Drives the GYTR step flow (pre- and post-GW1): one Sanity block per step,
 * the import → build-manually branch, hash-based back/forward navigation,
 * and the shared progress chrome. Mirrors ReadinessFlowController.
 */
const GytrFlowController: FC<GytrFlowControllerProps> = ({ quizPage }) => {
  const { blocks, templateKey } = quizPage;
  const { state, actions } = useGytr();
  const router = useRouter();

  // Logged-out members bounced here from a protected page (e.g. an
  // onboarding email CTA) arrive with ?returnTo=<original path>. Carrying it
  // into the login links lets them log in and land back on the action that
  // sent them here instead of restarting the funnel.
  const loginUrl = useMemo(() => {
    const { returnTo } = router.query;
    const rawReturnTo = Array.isArray(returnTo) ? returnTo[0] : returnTo;
    return isAppRelativeReturnTo(rawReturnTo)
      ? `/auth/login?returnTo=${encodeURIComponent(rawReturnTo)}`
      : '/auth/login';
  }, [router.query]);

  const firstStepIndex = useMemo(
    () =>
      Math.max(
        0,
        blocks.findIndex((block) => block._type === 'gytrImportStepBlock')
      ),
    [blocks]
  );

  const [currentIndex, setCurrentIndex] = useState(firstStepIndex);
  // Held on the provider rather than here: it is the paywall's Auth0
  // login_hint, and also the fallback identity the completion PATCH sends when
  // the session cookie does not travel, which the provider is what issues.
  // In-memory only — gone on refresh, like the flow.
  const capturedEmail = state.capturedEmail;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentIndex]);

  // FFH-2372: session-latched inside the helper, so the refresh guard's
  // bounce back to the first step does not read as a second start.
  useEffect(() => {
    captureFunnelStarted(GYTR_FUNNEL, router.pathname);
  }, [router.pathname]);

  const emailCaptureMutation = useFunnelEmailCapture();

  const handleGoToStep = useCallback((index: number) => {
    setCurrentIndex(index);
  }, []);

  useQuizHashTracking(blocks, currentIndex, handleGoToStep);

  const currentBlock = blocks[currentIndex];

  // Refresh/deep-link guard: in-memory state is gone after a reload, so any
  // step past the import branch bounces back to the import step.
  useEffect(() => {
    if (!currentBlock) return;
    if (NO_TEAM_BLOCK_TYPES.has(currentBlock._type)) return;
    if (currentBlock._type === 'paywallBlock') return;
    if (state.teamSource === null) {
      setCurrentIndex(firstStepIndex);
    }
  }, [currentBlock, state.teamSource, firstStepIndex]);

  const progressSteps = useMemo(() => deriveProgressSteps(blocks), [blocks]);
  const currentProgressStep = getCurrentProgressStep(
    progressSteps,
    currentIndex
  );

  const goToBlockType = useCallback(
    (blockType: SanityQuizBlock['_type']) => {
      const index = blocks.findIndex((block) => block._type === blockType);
      if (index !== -1) setCurrentIndex(index);
    },
    [blocks]
  );

  const goToNextStep = useCallback(() => {
    const currentGroup = getCurrentProgressStep(progressSteps, currentIndex);
    const nextGroup = progressSteps[currentGroup + 1];

    // FFH-2372: advancing means the visitor finished the step they were on.
    // Lateral branch jumps (goToBlockType) are not completions and stay
    // unmeasured on purpose.
    const completedStep = progressSteps[currentGroup];
    if (completedStep) {
      captureFunnelStepCompleted(
        GYTR_FUNNEL,
        currentGroup,
        completedStep.groupId
      );
    }

    if (nextGroup) {
      setCurrentIndex(nextGroup.blockIndexes[0]);
      return;
    }
    if (currentIndex + 1 < blocks.length) {
      setCurrentIndex(currentIndex + 1);
    }
  }, [progressSteps, currentIndex, blocks.length]);

  /**
   * Leaves a team step for whatever comes next.
   *
   * Straight to the rating once an address has been captured: a squad corrected
   * on the review pitch has already passed the email gate, and sending it back
   * through asks for an address we hold and then re-runs the rating that sent
   * them there, which is a loop rather than a step (FFH-2738). No step
   * completion fires, because the import group was recorded the first time
   * through and firing again would count it twice.
   */
  const advanceFromTeamStep = useCallback(() => {
    if (state.capturedEmail) {
      goToBlockType('gytrProcessingBlock');
      return;
    }
    goToNextStep();
  }, [state.capturedEmail, goToBlockType, goToNextStep]);

  const handleEmailSubmit = useCallback(
    async (email: string) => {
      // Trimmed once so the capture payload and the Auth0 login_hint
      // (capturedEmail -> paywall) agree; type="email" inputs usually
      // sanitise this already, but not reliably in every webview.
      const trimmedEmail = email.trim();
      const funnelSlug = templateKey || quizPage.slug.current;
      actions.setCapturedEmail(trimmedEmail);
      // FFH-2372: fired on submit, not on the mutation settling — the capture
      // request is allowed to fail without trapping the visitor, so gating
      // the event on it would under-count. Carries no email: no PII.
      captureFunnelEmailCaptured(GYTR_FUNNEL);

      // Awaited, unlike before. This request is what issues the
      // `_funnel_session` cookie, and the next step starts the rating
      // immediately: since the rating stopped waiting for the solver it can
      // now answer — and complete the funnel — before an unawaited capture
      // has replied, leaving the completion with no cookie to report against
      // (FFH-2376).
      //
      // A failure does not trap the visitor: the funnel is theirs to finish,
      // and the completion still carries the email as its fallback identity.
      await awaitEmailCaptureWithinBudget(
        emailCaptureMutation
          .mutateAsync({ email: trimmedEmail, funnelSlug })
          .catch((error: unknown) => {
            // eslint-disable-next-line no-console
            console.error('Funnel email capture failed', { funnelSlug, error });
          })
      );

      goToNextStep();
    },
    [emailCaptureMutation, templateKey, quizPage.slug, goToNextStep, actions]
  );

  if (!currentBlock) return null;

  const renderStepBlock = (block: SanityQuizBlock) => {
    switch (block._type) {
      case 'gytrImportStepBlock':
        return (
          <GytrImportStep
            key={block._key}
            {...block}
            loginUrl={loginUrl}
            onImported={advanceFromTeamStep}
            onNeedsReview={() => goToBlockType('gytrManualEntryStepBlock')}
            onBuildManually={() => goToBlockType('gytrManualEntryStepBlock')}
          />
        );

      case 'gytrManualEntryStepBlock':
        return (
          <GytrManualEntryStep
            key={block._key}
            {...block}
            onContinue={advanceFromTeamStep}
            onBack={() => goToBlockType('gytrImportStepBlock')}
          />
        );

      case 'gytrEmailGateBlock':
        return (
          <GytrEmailGateStep
            key={block._key}
            {...block}
            loginUrl={loginUrl}
            onSubmit={handleEmailSubmit}
            isSubmitting={emailCaptureMutation.isLoading}
          />
        );

      case 'gytrProcessingBlock':
        return (
          <GytrProcessingStep
            key={block._key}
            {...block}
            onComplete={goToNextStep}
            onSquadRefused={() => goToBlockType('gytrManualEntryStepBlock')}
            onStartAgain={() => goToBlockType('gytrImportStepBlock')}
            onTeamUnavailable={() => goToBlockType('gytrImportStepBlock')}
          />
        );

      case 'gytrRevealBlock':
        return (
          <GytrRevealStep key={block._key} {...block} onNext={goToNextStep} />
        );

      case 'gytrTransferBlock':
        return (
          <GytrTransferStep key={block._key} {...block} onNext={goToNextStep} />
        );

      case 'paywallBlock':
        return (
          <PaywallBlock
            key={block._key}
            {...block}
            theme={quizPage.theme}
            quizState={INITIAL_QUIZ_STATE}
            templateKey={templateKey}
            paywallContext={buildGytrPaywallContext(state)}
            loginHintEmail={capturedEmail ?? undefined}
            emailOnlySignup
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="relative z-20 min-h-screen bg-neutral-100">
      <div className="sticky top-0 z-30">
        <StepProgressTopBar
          totalSteps={progressSteps.length}
          currentStep={currentProgressStep}
        />
      </div>
      {renderStepBlock(currentBlock)}
    </div>
  );
};

export default GytrFlowController;
